import { z } from "zod";

// Zod schemas for raw BE shapes (snake_case). Used to validate before transform.

const assessmentStatusApiSchema = z.enum([
  "assessment_rooms_creation_in_progress",
  "input_needed",
  "required_questions_answered",
  "generating_followup_questions",
  "followup_questions_input_needed",
  "completed",
  "submitted",
]);

export const authenticateResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    client: z.object({
      name: z.string().catch(""),
      logo_url: z.string().nullable().catch(null),
      primary_color: z.string().nullable().optional().catch(null),
      background_color: z.string().nullable().optional().catch(null),
      secondary_color: z.string().nullable().optional().catch(null),
      accent_color: z.string().nullable().optional().catch(null),
    }),
    vendor: z.object({
      name: z.string().catch(""),
      logo_url: z.string().nullable().catch(null),
      user: z.object({
        first_name: z.string().catch(""),
        last_name: z.string().catch(""),
        email: z.string(),
      }),
    }),
    assessment: z.object({
      status: assessmentStatusApiSchema,
      submitted_at: z.string().nullable().optional().catch(null),
      chat_id: z.string().nullable().optional().catch(null),
      metrics: z.object({
        documents: z.object({
          completed: z.number().catch(0),
          total: z.number().catch(0),
          remaining: z.number().catch(0),
        }),
        questions: z.object({
          completed: z.number().catch(0),
          total: z.number().catch(0),
          remaining: z.number().catch(0),
        }),
      }),
    }),
  }),
});

export const assessmentRoomsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    assessment_rooms: z.array(
      z.object({
        id: z.string(),
        name: z.string().catch(""),
        questions: z.object({
          answered: z.number().catch(0),
          total: z.number().catch(0),
          required_answered: z.number().catch(0),
          total_required: z.number().catch(0),
        }),
        documents: z.object({
          uploaded: z.number().catch(0),
          total: z.number().catch(0),
          required_uploaded: z.number().optional().default(0),
          total_required: z.number().optional().default(0),
        }),
      }),
    ),
    recommendation: z
      .object({
        header: z.string().catch(""),
        message: z.string().catch(""),
        assessment_room_id: z.string(),
      })
      .nullable()
      .optional()
      .catch(null),
    all_required_questions_answered: z.boolean().optional().catch(false),
    all_required_evidences_uploaded: z.boolean().optional().catch(false),
  }),
});

export const assessmentDetailsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    assessment: z.object({
      id: z.string(),
      total_questions: z.number().catch(0),
      total_questions_answered: z.number().catch(0),
      percentage_completion: z.number().catch(0),
      due_date: z.string().catch(""),
      days_left: z.number().catch(0),
    }),
    client: z.object({
      org: z.object({ name: z.string().catch("") }),
      user: z.object({
        first_name: z.string().catch(""),
        last_name: z.string().catch(""),
        email: z.string().catch(""),
      }),
    }),
    vendor: z
      .object({
        tier: z.object({
          value: z.number().nullable().catch(null),
          label: z.string().catch(""),
        }),
      })
      .catch({ tier: { value: null, label: "" } }),
  }),
});

const questionApiSchema = z.object({
  id: z.string(),
  question_number: z.string().catch(""),
  text: z.string().catch(""),
  question_type: z.enum([
    "free_text",
    "single_choice",
    "multiple_choice",
    "file_attachment",
  ]),
  dropdown_single_choice: z.boolean().catch(false),
  choices: z.array(z.string()).catch([]),
  required: z.boolean().catch(false),
  answer: z.union([z.string(), z.array(z.string()), z.null()]).catch(null),
  comments: z.string().nullable().catch(null),
  pre_filled_response: z.string().nullable().catch(null),
});

const evidenceApiSchema = z.object({
  id: z.string(),
  document_name: z.string().catch(""),
  source_urls: z.array(z.string()).catch([]),
  file_names: z.array(z.string()).catch([]),
  number_of_files: z.number().catch(0),
  required: z.boolean().catch(false),
  max_number_of_files: z.number().catch(0),
});

export const categoriesResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    assessment_room: z.object({
      id: z.string(),
      name: z.string().catch(""),
      categories: z.array(
        z.object({
          id: z.string(),
          name: z.string().catch(""),
          questions: z.array(questionApiSchema),
        }),
      ),
      all_documents: z
        .array(
          z.object({
            file_name: z.string().catch(""),
            file_url: z.string().catch(""),
          }),
        )
        .optional()
        .catch([]),
      evidences: z.array(evidenceApiSchema).optional().catch([]),
    }),
  }),
});

// PATCH response uses `total_questions_answered`
const questionPatchApiSchema = z.object({
  id: z.string(),
  question_number: z.string().catch(""),
  text: z.string().catch(""),
  question_type: z.enum([
    "free_text",
    "single_choice",
    "multiple_choice",
    "file_attachment",
  ]),
  dropdown_single_choice: z.boolean().catch(false),
  choices: z.array(z.string()).catch([]),
  required: z.boolean().catch(false),
  answer: z.union([z.string(), z.array(z.string()), z.null()]).catch(null),
  comments: z.string().nullable().catch(null),
  pre_filled_response: z.string().nullable().catch(null),
});

export const saveAnswerResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    question: questionPatchApiSchema,
    assessment_room_metadata: z.object({
      total_questions: z.number().catch(0),
      total_questions_answered: z.number().catch(0),
    }),
  }),
});

// Chat schemas
const chatAttachmentApiSchema = z.object({
  // GET /chat/messages returns attachments WITHOUT an id — keep it optional so the
  // whole attachment isn't dropped by the array-level .catch([]). A stable id is
  // synthesized in transformChatMessage.
  id: z.string().optional(),
  file_name: z.string().catch(""),
  signed_url: z.string().catch(""),
});

const chatReasoningApiSchema = z.object({
  id: z.string(),
  follow_up_question_id: z.string().catch(""),
  follow_up_question_index: z.number().catch(0),
  reason_for_vendor: z.object({
    title: z.string().catch(""),
    description: z.string().catch(""),
  }),
});

const chatMessageApiSchema = z.object({
  id: z.string(),
  chat_id: z.string().optional().catch(undefined),
  role: z.enum(["user", "assistant", "agent"]),
  type: z.literal("text"),
  text: z.string().catch(""),
  created_at: z.string().catch(""),
  attachments: z.array(chatAttachmentApiSchema).catch([]),
  reasoning: z.array(chatReasoningApiSchema).nullish().catch(null),
});

export const chatMessagesResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional().catch(undefined),
  data: z.object({
    total_count: z.number().catch(0),
    chat_id: z.string(),
    session_id: z.string(),
    organization_id: z.string(),
    messages: z.array(chatMessageApiSchema).catch([]),
  }),
});

const sendChatMessageDataApiSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant", "agent"]),
  type: z.literal("text"),
  text: z.string().catch(""),
  created_at: z.string().catch(""),
  attachments: z.array(chatAttachmentApiSchema).catch([]),
});

export const sendChatMessageResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional().catch(undefined),
  data: sendChatMessageDataApiSchema,
});

export const signedUrlResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional().catch(undefined),
  data: z.object({
    signed_urls: z.array(z.string()),
    file_names: z.array(z.string()),
  }),
});

/** Shared data shape returned by both PATCH and DELETE evidence endpoints */
const evidenceResponseDataSchema = z.object({
  id: z.string(),
  document_name: z.string().catch(""),
  source_urls: z.array(z.string()).catch([]),
  file_names: z.array(z.string()).catch([]),
  number_of_files: z.number().catch(0),
  required: z.boolean().catch(false),
  max_number_of_files: z.number().catch(0),
});

export const patchEvidenceResponseSchema = z.object({
  success: z.boolean(),
  data: evidenceResponseDataSchema,
});

export const deleteEvidenceResponseSchema = z.object({
  success: z.boolean(),
  data: evidenceResponseDataSchema,
});
